import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPublicCode } from "@/lib/public-code";
import { formatSiteStatus, getSiteStatusBadgeVariant } from "@/lib/site-status";
import type { Site } from "@/types";
import { trpc } from "@/utils/trpc";

function SitesSection({
  managedSites,
}: {
  managedSites: ReturnType<typeof useQuery>;
}) {
  if (managedSites.isError) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
        Unable to load the sites list right now.
      </div>
    );
  }

  if (managedSites.isLoading) {
    return <div className="text-muted-foreground">Loading sites…</div>;
  }

  if (!Array.isArray(managedSites.data) || managedSites.data.length === 0) {
    return (
      <div className="surface-panel flex flex-col items-center gap-2 border-dashed p-10 text-center text-sm text-muted-foreground">
        <p className="font-medium text-foreground">No sites yet</p>
        <p className="max-w-md text-xs text-muted-foreground">
          Sites you create will appear here. Use “Add” to set up your first
          site.
        </p>
      </div>
    );
  }

  return <SitesTable sites={managedSites.data} />;
}

export default function ManagedSitesSection() {
  const managedSites = useQuery(trpc.getManagedSites.queryOptions());
  const navigate = useNavigate();

  const handleAddSite = () => navigate("/sites/new");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Manage sites"
        description="View every managed site, open its workspace, and create new ones."
        toolbar={
          <Button size="sm" onClick={handleAddSite}>
            Add
          </Button>
        }
      />
      <SitesSection managedSites={managedSites} />
    </div>
  );
}

function SitesTable({ sites }: { sites: Site[] }) {
  const rows = useMemo(
    () =>
      [...sites].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      ),
    [sites]
  );

  return (
    <div className="table-panel">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-56 px-4">Site</TableHead>
            <TableHead className="min-w-32 px-4">Status</TableHead>
            <TableHead className="min-w-72 px-4">Address</TableHead>
            <TableHead className="min-w-44 px-4">Coordinates</TableHead>
            <TableHead className="min-w-28 px-4">Geofence</TableHead>
            <TableHead className="min-w-28 px-4">Entrances</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((site) => {
            const entranceCount = site.entranceCount ?? 0;

            return (
              <TableRow key={site.id} className="align-top">
                <TableCell className="px-4 py-3">
                  <div className="space-y-1">
                    <Link
                      to={`/sites/${site.id}`}
                      className="font-medium text-foreground hover:underline"
                    >
                      {site.name}
                    </Link>
                    <p className="font-meta text-xs text-muted-foreground">
                      {formatPublicCode(site.publicCode) ?? "Code pending"}
                    </p>
                  </div>
                </TableCell>
                <TableCell className="px-4 py-3">
                  <Badge variant={getSiteStatusBadgeVariant(site.status)}>
                    {formatSiteStatus(site.status)}
                  </Badge>
                </TableCell>
                <TableCell className="px-4 py-3 text-sm text-muted-foreground">
                  <span className="block max-w-[20rem] whitespace-normal break-words">
                    {site.address}
                  </span>
                </TableCell>
                <TableCell className="font-meta px-4 py-3 text-sm text-muted-foreground">
                  <div className="space-y-1">
                    <p className="text-foreground">
                      {site.lat.toFixed(5)}, {site.lon.toFixed(5)}
                    </p>
                    <p className="text-xs">
                      {site.plusCode ? site.plusCode : "Coordinates only"}
                    </p>
                  </div>
                </TableCell>
                <TableCell className="font-meta px-4 py-3 text-sm text-muted-foreground">
                  {site.geofenceDistance}m
                </TableCell>
                <TableCell className="font-meta px-4 py-3 text-sm text-muted-foreground">
                  {entranceCount}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
